<template>
    <div class="allday-page">
        <div id="sticky-nav">
            <div class="nav-wrapper page-container">
                <b-nav card-header v-b-scrollspy="100">
                    <b-nav-item disabled class="fw-bold text-uppercase text-primary">Jump To:</b-nav-item>
                    <b-nav-item href="#study-1-design" @click="scrollToSecion($event, 25)"
                        class="d-none d-lg-flex">Study
                        Design</b-nav-item>
                    <span class="separator d-none d-lg-flex">|</span>
                    <b-nav-item href="#clinically" @click="scrollToSecion($event)"
                        class="d-none d-lg-flex text-center">Impairment Scores</b-nav-item>
                    <span class="separator d-none d-lg-flex">|</span>
                    <b-nav-item href="#jornay-pm" @click="scrollToSecion($event)" class="d-none d-lg-flex">Classroom
                        Behavior Scores</b-nav-item>
                    <CustomSelect :options="[
                        'Study Design',
                        'Impairment Scores',
                        'Classroom Behavior Scores',
                    ]" :ids="['#study-1-design', '#clinically', '#jornay-pm']" :offsets="['28', '20', '15']"
                        :selected="customSelectDefaultValue" class="d-lg-none" @input="handleSelect"
                        @blur="handleBlur" />
                </b-nav>
            </div>
        </div>

        <div class="page-container" id="study-1-design">
            <div class="">
                <span class="eyebrow-page omnes fw-semibold text-orange">Efficacy</span>

                <h1 class="gtitle fw-semibold">
                    Efficacy and safety of JORNAY PM were established in two Phase 3 trials<sup class="omnes">1-3</sup>
                </h1>
            </div>

            <section class="study-1-design">
                <h2 class="open-sans fw-bold">Study 1 design<sup>1,2</sup></h2>

                <ul class="study-1-design__list">
                    <li class="study-1-design__list--item">
                        <h3>ENROLLMENT<sup>2</sup></h3>

                        <ul>
                            <li class="pl-4">
                                Children 6-12 years of age with ADHD (N=117)
                            </li>
                            <li class="pl-4">
                                JORNAY PM (n=64), placebo (n=53)
                            </li>
                        </ul>
                    </li>

                    <li class="study-1-design__list--item">
                        <h3>DOSE OPTIMIZATION PHASE<sup>1</sup></h3>

                        <ul>
                            <li class="pl-4">
                                Open-label JORNAY PM monotherapy for 6 weeks
                            </li>

                            <li class="pl-4">20 mg, 40 mg, 60 mg, 80 mg, and 100 mg</li>
                        </ul>
                    </li>

                    <li class="study-1-design__list--item">
                        <h3>TREATMENT PHASE<sup>1,2</sup></h3>

                        <ul>
                            <li class="pl-4">
                                Double-blind, placebo-controlled treatment phase
                                for 1 week<sup>1</sup>
                            </li>

                            <li class="pl-4">
                                Assessed safety and tolerability of JORNAY PM throughout the day from morning to evening
                                at
                                Visit 9<sup>2</sup>
                            </li>
                        </ul>
                    </li>
                </ul>

                <div class="mt-3">
                    <p style="line-height: 20px;">Study 2 was a 3-week, double-blind, randomized, placebo-controlled
                        study that evaluated the
                        efficacy and safety of JORNAY PM with
                        ADHD-RS-IV as primary endpoint<sup>3</sup></p>
                </div>
            </section>

            <section class="clinically">


                <h2 class="open-sans fw-bold">
                    Clinically validated rating scales were used to assess the
                    efficacy of JORNAY PM from morning to evening<sup>2</sup>
                </h2>

                <ul class="clinically__list">
                    <li class="clinically__list-item mt-4">
                        <img src="@/assets/img/efficacy/icon-morning.png" alt="Orange icon of a curved line" width="90"
                            height="90" />

                        <div class="clinically__list-item--jornay">
                            <h3 class="text-orange fw-bold">
                                PRIMARY ENDPOINT:
                            </h3>

                            <ul>
                                <li>
                                    Model-adjusted average of all postdose SKAMP CS measured over a 12-hour laboratory
                                    classroom day at Visit 9 from 8:00 <span class="ampm">AM</span> to 8:00 <span
                                        class="ampm">PM</span><sup>2</sup>
                                </li>
                            </ul>
                        </div>
                    </li>

                    <li class="clinically__list-item clinically__list-item--box">
                        <p class="fw-semibold">
                            <strong class="fw-bold">SKAMP CS over a 12-hour laboratory classroom day:</strong>
                            The SKAMP scale assesses behaviors (eg, attentiveness, quality of work, compliance). The
                            lower the score, the greater the improvement.<sup>2,4</sup>
                        </p>
                    </li>

                    <li class="clinically__list-item">
                        <img src="@/assets/img/efficacy/icon-evening.png" alt="Orange icon of the sun and moon"
                            width="90" height="90" />

                        <div class="clinically__list-item--text">
                            <h3 class="text-orange fw-bold">
                                SECONDARY ENDPOINTS:
                            </h3>

                            <p>
                                Clinician-rated assessment based on parent
                                interviews in which the parents have rated their
                                child’s functional impairment during the early
                                morning and in the later afternoon and
                                evening<sup>2</sup>:
                            </p>

                            <ul>
                                <li class="mb-2">
                                    The <strong>PREMB-R AM</strong> assesses the
                                    difficulty of <span class="text-nowrap">early-morning</span> functioning*
                                </li>

                                <li>
                                    The <strong>PREMB-R PM</strong> assesses the
                                    difficulty of <span class="text-nowrap">late-afternoon</span> and evening
                                    functioning<sup>†</sup>
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>

                <div class="notes">
                    <p>
                        <span class="asterisk">*</span>Example: Getting out of bed, getting ready for the day, and
                        arguing
                        or struggling excessively.<sup>5</sup>
                    </p>

                    <p>
                        <span><sup>†</sup></span>Example: Completing homework, sitting through an evening meal, getting
                        ready for bed, etc.<sup>5</sup>
                    </p>

                    <p>
                        ADHD-RS-IV=attention-deficit/hyperactivity disorder rating scale, version IV; PREMB-R AM=Parent
                        Rating of Evening and Morning Behavior-Revised, Morning; PREMB-R PM=Parent Rating of Evening and
                        Morning Behavior-Revised, Evening; SKAMP CS=Swanson, Kotkin, Agler, M-Flynn, and Pelham combined
                        scores.
                    </p>

                </div>
            </section>
        </div>

        <section class="jornay-pm" id="clinically">
            <div class="page-container">
                <h2 class="eyebrow-page fw-semibold">
                    <span class="omnes">
                        JORNAY PM significantly improved ADHD assessment scores from awakening until the late
                        afternoon/evening<sup class="omnes">2</sup>
                    </span>
                </h2>

                <h3 class="fw-bold">
                    Significant improvements in impairment vs placebo in the early morning, classroom day, and into the
                    later afternoon/evening<sup>2</sup>
                </h3>

                <ul class="">
                    <li class="item item-1">
                        <img class="icon" src="@/assets/img/efficacy/icon-morning-2.png" alt="Orange icon of the sun"
                            width="90" height="90" />

                        <div class="top-title">
                            <h3 class="fw-bold text-orange">
                                SECONDARY ENDPOINT<sup>2,6</sup>:
                            </h3>

                            <p>early morning functional impairment</p>
                        </div>

                        <h4 class="fw-semibold">
                            PREMB-R AM SCORE<sup>‡</sup>
                        </h4>

                        <img class="graph" src="@/assets/img/efficacy/Graph1.png"
                            alt="Bar graph showcasing the PREMB-R AM Score in the early morning using JORNAY PM vs a placebo" />

                        <div class="note">
                            <p>Mean final dose was 66.2 mg.<sup>2</sup></p>
                        </div>
                    </li>

                    <li class="item item-2">
                        <img class="icon" src="@/assets/img/efficacy/icon-morning.png"
                            alt="Orange icon of a curved line" width="90" height="90" />

                        <div class="top-title">
                            <h3 class="fw-bold text-orange">
                                PRIMARY ENDPOINT<sup>1,2</sup>:
                            </h3>

                            <p>
                                classroom day (8
                                <span class="small-text">AM</span> to 8
                                <span class="small-text">PM</span>)
                            </p>
                        </div>

                        <h4 class="fw-semibold">SKAMP COMBINED SCORE</h4>

                        <img class="graph" src="@/assets/img/efficacy/Graph2.png"
                            alt="Bar graph showcasing the SKAMP Combined Score during the day using JORNAY PM vs a placebo" />

                        <div class="note">
                            <p>Mean final dose was 66.2 mg.<sup>2</sup></p>
                        </div>
                    </li>

                    <li class="item item-3">
                        <img class="icon" src="@/assets/img/efficacy/icon-evening-2.png" alt="Orange icon of the moon"
                            width="90" height="90" />

                        <div class="top-title">
                            <h3 class="fw-bold text-orange">
                                SECONDARY ENDPOINT<sup>2,6</sup>:
                            </h3>

                            <p>late afternoon/evening functional impairment</p>
                        </div>

                        <h4 class="fw-semibold">
                            PREMB-R PM SCORE<sup>§</sup>
                        </h4>

                        <img class="graph" src="@/assets/img/efficacy/Graph3.png"
                            alt="Bar graph showcasing the PREMB-R PM Score in the late afternoon/evening using JORNAY PM vs a placebo" />
                    </li>
                </ul>

                <div class="note">
                    <p>Mean final dose was 66.2 mg.<sup>2</sup></p>
                </div>

                <div class="cloudy-box">
                    <p class="fw-bold m-0">
                        No short-acting booster was permitted in the pivotal trial.
                    </p>

                    <p class="fw-bold">
                        What would not needing a short-acting booster mean to your patients?
                    </p>
                </div>

                <div class="clinical-rating-scales">
                    <h3 class="fw-bold">Clinical rating scales:</h3>

                    <ul>
                        <div class="col-12 col-lg-4 p-0">
                            <li>
                                The <strong>PREMB-R AM</strong> assesses the
                                difficulty of <span class="text-nowrap">early-morning</span> functioning
                            </li>

                        </div>
                        <div class="col-12 col-lg-4 p-0">
                            <li>
                                The <strong>PREMB-R PM</strong> assesses the
                                difficulty of <span class="text-nowrap">late-afternoon</span> and evening functioning
                            </li>

                        </div>
                        <div class="col-12 col-lg-4 p-0">


                            <li>
                                The <strong>SKAMP</strong> combined scale assesses
                                behaviors critical to success in school settings
                            </li>
                        </div>

                    </ul>

                    <div class="notes">
                        <p>
                            <span class=""><sup>‡</sup></span>PREMB-R AM baseline values (ITT population) were
                            5.8 (JORNAY PM) and 5.7 (placebo).<sup>7</sup>
                        </p>

                        <p>
                            <span><sup>§</sup></span>PREMB-R PM baseline values (ITT population) were
                            17.6 (JORNAY PM) and 16.6 (placebo).<sup>7</sup>
                        </p>

                        <p>
                            <span><sup>¶</sup></span>
                            Based on a normative online survey of 1,200 primary caregivers, mean (SD) scores of non-ADHD
                            youth were 2.3 (2.13) and 5.1 (4.80) for PREMB-R AM and PREMB-R PM,
                            respectively.<sup>6</sup> Benchmarks are not study endpoints and no statistical comparisons were made.They are included to add context to the secondary endpoints.
                        </p>

                        <p class="mt-2">ITT=intent-to-treat; SD=standard deviation.</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="improved-behaviors" id="jornay-pm">
            <div class="page-container">
                <h2 class="eyebrow-page fw-semibold">
                    <span class="omnes">
                        JORNAY PM significantly improved behaviors in a laboratory classroom setting vs placebo<sup
                            class="omnes">2</sup>
                    </span>
                </h2>

                <h3 class="fw-bold">
                    Participants on JORNAY PM showed a statistically significant improvement at each hourly interval
                    between
                    9:00 <span class="small-text-2">AM</span> and 7:00
                    <span class="small-text-2">PM</span> <sup>2</sup>
                </h3>

                <ul>
                    <li>
                        Improvement at 8:00 <span style="font-size: 12px;">AM</span> and 8:00 <span
                            style="font-size: 12px;">PM</span> intervals was not
                        significant<sup>2</sup>
                    </li>
                </ul>

                <div class="graphic">
                    <h4 class="fw-bold">
                        SKAMP CS FROM 8:00
                        <span class="small-text-3">AM</span> TO 8:00
                        <span class="small-text-3">PM</span> AT VISIT 9
                    </h4>

                    <img class="d-none d-lg-block" src="@/assets/img/efficacy/JORNAY_Vector_Charts-2.png"
                        alt="Line graph showcasing the SKAMP CS from 8:00 AM to 8:00 PM using JORNAY PM vs a placebo" />


                    <div class="d-block d-lg-none" @mouseleave="restoreZoomIcon" @click="tapIcon">
                        <img v-if="showZoomIcon" src="@/assets/img/efficacy/JORNAY_Vector_Charts-2-mobile.png"
                            alt="Line graph showcasing the SKAMP CS from 8:00 AM to 8:00 PM using JORNAY PM vs a placebo"
                            @wheel="hideZoomIcon" class="zoomable-image" />

                        <!-- Image without zoom icon -->
                        <img v-else src="@/assets/img/efficacy/JORNAY_Vector_Charts-2-mobile-noicon.png"
                            alt="Line graph showcasing the SKAMP CS from 8:00 AM to 8:00 PM using JORNAY PM vs a placebo"
                            class="zoomable-image" />

                    </div>
                    <!-- <img
                        class="d-block d-lg-none"
                        src="@/assets/img/efficacy/JORNAY_Vector_Charts-2-mobile.png"
                        alt=""
                    /> -->
                </div>

            </div>



            <div class="container cloudy-container">
                <div class="cloudy-box">
                    <p class="fw-bold">
                        What would significant ADHD symptom improvement from <span class="text-nowrap">9
                        <span class="small-text-3">AM</span></span> to <span class="text-nowrap">7
                        <span class="small-text-3">PM</span></span>
                        mean to your patients?<sup>2</sup>
                    </p>
                </div>
            </div>
            
        </section>

        <CTABanner :onlyBanner="true" :bannerData="{
            bannerText: 'SEE PK/PD MODEL',
            href: '/methylphenidate-dosage-duration',
        }" />
    </div>
</template>

<script>
import StudyOneModal from '../components/efficacy/StudyOneModal.vue';
import StudyTwoModal from '../components/efficacy/StudyTwoModal.vue';
import PrembR from '../components/efficacy/PrembR.vue';
import SKAMP from '../components/efficacy/SKAMP.vue';
import RSIV from '../components/efficacy/RSIV.vue';
import BSFQ from '../components/efficacy/BSFQv2.vue';
import StudyDesign from '../components/efficacy/StudyDesign.vue';
import headers from '../models/headers';
import FooterForm from '../components/global/FooterForm.vue';

import vueCustomScrollbar from 'vue-custom-scrollbar';
import 'vue-custom-scrollbar/dist/vueScrollbar.css';

import CTABanner from '../components/CTABanner.vue';

import CustomSelect from '../components/CustomSelect.vue';

export default {
    created() {
        this.$root.$on('bv::scrollspy::activate', this.onActivate);
    },
    components: {
        StudyOneModal,
        StudyTwoModal,
        PrembR,
        FooterForm,
        BSFQ,
        RSIV,
        SKAMP,
        StudyDesign,
        vueCustomScrollbar,
        CTABanner,
        CustomSelect,
    },
    metaInfo: headers.efficacyPage(),
    data() {
        return {
            studyone: false,
            studytwo: false,
            modal1: false,
            modal2: false,
            modal3: false,
            modal4: false,
            studyDesign: false,
            settings: {
                suppressScrollY: true,
                suppressScrollX: false,
                wheelPropagation: true,
            },
            mobilePrem: true,
            mobileBSFQ: true,
            customSelectDefaultValue: '#study-1-design',
            showZoomIcon: true,
            timeoutId: null,
        };
    },
    methods: {
        openModal: function () { },
        scrollHanle(evt) {
            console.log(evt);
        },
        onActivate(target) {
            this.customSelectDefaultValue = target;
        },
        scrollToSecion(evt, offset = 15) {
            evt.preventDefault();
            const href = evt.target.getAttribute('href');
            const el = href ? document.querySelector(href) : null;
            if (el) {
                const elementPosition =
                    el.getBoundingClientRect().top + window.scrollY;
                const offsetPosition = elementPosition - offset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            }
        },
        handleSelect(data) {
            const el = data ? document.querySelector(data.id) : null;
            if (el) {
                const elementPosition =
                    el.getBoundingClientRect().top + window.scrollY;
                const offsetPosition = elementPosition - data.offset - 120; //120 opened drawer, TODO: make it dynamic
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            }
        },
        handleBlur() {
            console.log('>>> handleBlur');
        },
        hideZoomIcon() {
            this.showZoomIcon = false;
            clearTimeout(this.timeoutId);
        },
        restoreZoomIcon() {
            this.timeoutId = setTimeout(() => {
                this.showZoomIcon = true;
            }, 500);
        },
        tapIcon() {
            this.showZoomIcon = false;
        }
    },
};
</script>
